import React, { FunctionComponent, useState } from 'react'
import Button from '@material-ui/core/Button'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { IParkingLotSessionSpecialRatesRateData, useApi } from 'components/providers/ApiProvider'
import { useParkingLot } from '../ParkingLotProvider'
import { useMessage } from 'components/elements/Message'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import { useTranslation } from 'react-i18next'
import SelectField from 'components/elements/SelectField'
import Alert from '@material-ui/lab/Alert'
import DialogLoader from 'Elements/DialogLoader'
import Box from '@material-ui/core/Box'

export interface EditSpecialRateDialogData {
  sessionId: number
  sessionHasSpecialRate: boolean
}

type Props = {
  open: boolean
  data: EditSpecialRateDialogData
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const EditSpecialRateDialog: FunctionComponent<AllProps> = ({ open, setOpen, data }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { setLoadList } = useParkingLot()
  const { showSuccess, showError } = useMessage()

  const [specialRates, setSpecialRates] = useState<IParkingLotSessionSpecialRatesRateData[]>(null)

  const handleEnter = () => {
    api.getParkingLotSessionSpecialRates({
      session_id: data.sessionId,
    })
      .then(data => {
        setSpecialRates(data.special_rates)
      })
      .catch(() => {
      })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    setSpecialRates(null)
    formal.change('specialRate', initialValues.specialRate)
    formal.clearErrors()
  }

  const handleCancelSpecialRate = () => {
    if (!confirm(t('confirms.cancelSpecialRate'))) {
      return
    }

    api.cancelParkingLotSessionSpecialRate({
      session_id: data.sessionId,
    })
      .then(data => {
        if (!data || !data.status) {
          throw new Error
        }

        handleClose()

        setLoadList()

        showSuccess(t('messages.specialRateCanceled'))
      })
      .catch(error => {
        let message

        switch (error.code) {
          case 14:
            message = t('alerts.parkingSessionHasAdditionalPayment')
            break
          default:
            message = t('alerts.tryLater')
        }

        showError(`${t('alerts.couldNotCancelSpecialRate')} ${message}`)
      })
  }

  const initialValues = {
    specialRate: 0,
  }

  const schema = yup.object().shape({
    specialRate: yup.number().required().min(1),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      await api.assignParkingLotSessionSpecialRate({
        session_id: data.sessionId,
        offer_id: values.specialRate,
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          setLoadList()

          showSuccess(t('messages.specialRateAssigned'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            case 14:
              message = t('alerts.parkingSessionHasAdditionalPayment')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotAssignSpecialRate')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('assignSpecialRate')}</DraggableDialogTitle>
        <DialogContent>
          {!specialRates && <DialogLoader />}

          {specialRates && !specialRates.length && (
            <Box paddingX={3} paddingY={2}>
              <Alert severity="info">
                {t('noSpecialRates')}
              </Alert>
            </Box>
          )}

          {specialRates && !!specialRates.length && (
            <Grid container>
              <SelectField
                GridProps={{ xs: 12 }}
                label={t('rate')}
                required
                optionsCustom={[
                  {
                    label: t('select'),
                    id: 0,
                  },
                  ...specialRates.map(offer => ({
                    id: offer.id,
                    label: <div>
                      {offer.name}
                      {offer.copayer_phone_number && <small style={{ display: 'block' }}><span style={{ color: '#aaa' }}>{t('co-payer')}:</span> {offer.copayer_phone_number}</small>}
                      {offer.assigners && <small style={{ display: 'block' }}><span style={{ color: '#aaa' }}>{t('assigners')}:</span> <span title={offer.assigners} style={{ display: 'inline-block', maxWidth: 300, verticalAlign: 'bottom', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{offer.assigners}</span></small>}
                    </div>,
                  })),
                ]}
                {...formal.getFieldProps('specialRate')}
              />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {data.sessionHasSpecialRate && (
            <Button
              variant="contained"
              color="secondary"
              style={{ marginRight: 'auto' }}
              onClick={handleCancelSpecialRate}
            >
              {t('cancelSpecialRate')}
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          {specialRates && !!specialRates.length && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formal.isSubmitting}
            >
              {t('assign')}
            </Button>
          )}
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default EditSpecialRateDialog
